"use client"

import { useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { completeTask, type Task } from "@/lib/tasks-api"

export function TaskCompletionDialog({ task, onClose, onSaved }: { task: Task; onClose: () => void; onSaved: () => void }) {
  const t = useTranslations("tasks")
  const [note, setNote] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const busy = useRef(false)
  const submit = async () => {
    if (busy.current || stale) return
    busy.current = true
    setPending(true)
    setError(null)
    try {
      await completeTask(task.id, { completion_note: note.trim() || null, lifecycle_version: task.lifecycle_version })
      toast.success(t("completed"))
      onSaved()
      onClose()
    } catch (err) {
      const conflict = (err as { response?: { status?: number } }).response?.status === 409
      setError(t(conflict ? "stale" : "actionFailed"))
      if (conflict) { setStale(true); onSaved() }
    } finally { busy.current = false; setPending(false) }
  }
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("completion.title")}</DialogTitle>
          <DialogDescription>{task.title}</DialogDescription>
        </DialogHeader>
        <Label htmlFor="task-completion-note">{t("completion.note")}</Label>
        <Textarea id="task-completion-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={5000} rows={4} disabled={pending || stale} />
        <p className="text-sm text-muted-foreground">{t("completion.hint")}</p>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={pending} onClick={onClose}>{t("form.cancelButton")}</Button>
          <Button disabled={pending || stale} onClick={submit}>{t(pending ? "completion.saving" : "actions.done")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
